import { SelectField } from '@/types/forms/fields/selectField';
import { MeasuringConceptsFormStep } from '@/types/forms/formSteps/measuringConceptsFormStep';
import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import {
    isPluginGeneratingPlant,
    isPvAndStorageLte30,
    isStorage,
    lawParagraph14aConditionsMet,
} from './common';
import { ValidationProperties, ValidationEvaluation } from 'vue/types/vue';
import { Validation } from 'vuelidate';
import { getOverallNominalPower } from '../../utilities/application/plantPerformance';

export const measuringConceptsRules = async (
    formStep: {
        model: MeasuringConceptsFormStep;
        formModel: ApplicationForm;
        validations:
            | (Validation &
                  ValidationProperties<unknown> &
                  ValidationEvaluation)
            | undefined;
    } & Vue,
): Promise<void> => {
    const zeroInfeed = formStep.model.operatingMode.options.find(
        (o) => o.key === 'zeroInfeed',
    );

    if (zeroInfeed) {
        zeroInfeed.disabled = true;
    }

    const commercialTransfer = formStep.model.operatingMode.options.find(
        (o) => o.key === 'commercialTransfer',
    );

    if (commercialTransfer) {
        commercialTransfer.disabled =
            formStep.formModel.applicationType === 'storage' ||
            isPluginGeneratingPlant(formStep.formModel);
    }

    const storageOperatingMode = isPvAndStorageLte30(formStep.formModel)
        ? formStep.model.operatingModeStorage
        : formStep.model.operatingMode;

    formStep.model.operatingModeStorage.disabled = !isPvAndStorageLte30(
        formStep.formModel,
    );

    if (isStorage(formStep.formModel)) {
        storageOperatingMode.options.forEach((o) => (o.disabled = true));

        const storageDischarge =
            formStep.formModel.plantData.storageDischarge.value;

        switch (storageDischarge) {
            case 'onlyIntoCustomersGridSelfSupply':
                SelectField.preselectExactlyOneOption(
                    storageOperatingMode,
                    'zeroInfeed',
                );
                break;
            case 'intoCustomersGridAndPublicGridBivalent':
                SelectField.preselectExactlyOneOption(
                    storageOperatingMode,
                    'surplusInfeed',
                );
                break;
            case 'onlyIntoPublicGridGridFeedIn':
                SelectField.preselectExactlyOneOption(
                    storageOperatingMode,
                    'fullInfeed',
                );
                break;
        }
    }

    if (isPluginGeneratingPlant(formStep.formModel)) {
        if (isStorage(formStep.formModel)) {
            SelectField.preselectExactlyOneOption(
                formStep.model.operatingMode,
                'zeroInfeed',
            );
        } else {
            SelectField.preselectExactlyOneOption(
                formStep.model.operatingMode,
                'surplusInfeed',
            );
        }
    }

    formStep.model.fullInfeedBonus.disabled =
        (formStep.formModel.applicationType !== 'photovoltaic' &&
            formStep.formModel.applicationType !==
                'photovoltaicAndStorageLte30') ||
        formStep.model.operatingMode.value !== 'fullInfeed' ||
        (formStep.formModel.generationType.plantStructureType.value !==
            'building' &&
            formStep.formModel.generationType.plantStructureType.value !==
                'noiseBarrier');

    formStep.model.fullInfeedBonusHeadline.disabled =
        formStep.model.fullInfeedBonus.disabled;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565613/Formular+Erzeugungsart#FormularErzeugungsart-meterNumber
    // https://netzkern.atlassian.net/browse/ED-644
    const isLowVoltageWithExistingConnection =
        !formStep.formModel.powerConnection.doesConnectionExist.disabled &&
        formStep.formModel.powerConnection.doesConnectionExist.value ===
            'yes' &&
        !formStep.formModel.powerConnection.whichVoltageLevel.disabled &&
        formStep.formModel.powerConnection.whichVoltageLevel.value ===
            'lowVoltage';
    formStep.model.meterNumber.disabled =
        !(
            isPluginGeneratingPlant(formStep.formModel) ||
            isLowVoltageWithExistingConnection
        ) || formStep.model.operatingMode.value == 'fullInfeed';

    formStep.model.measuringPointOperatorInfo.disabled = !lawParagraph14aConditionsMet(
        formStep.formModel,
    );

    formStep.model.measuringPointOperation.disabled =
        isStorage(formStep.formModel) &&
        !lawParagraph14aConditionsMet(formStep.formModel);

    formStep.model.measuringPointOperator.disabled =
        formStep.model.measuringPointOperation.disabled ||
        formStep.model.measuringPointOperation.value !== 'other';

    formStep.model.priceSheetMeasuringPointOperation.disabled =
        formStep.model.measuringPointOperation.disabled ||
        formStep.model.measuringPointOperation.value !== 'ewe';

    formStep.model.specialSupplyConcept.disabled =
        isPluginGeneratingPlant(formStep.formModel) ||
        formStep.model.operatingMode.value !== 'surplusInfeed';

    formStep.model.specialSupplyConceptInfo.disabled =
        formStep.model.specialSupplyConcept.disabled ||
        !formStep.model.specialSupplyConcept.value;

    formStep.model.specialSupplyConceptAcknowledgement.disabled =
        formStep.model.specialSupplyConcept.disabled ||
        !formStep.model.specialSupplyConcept.value;

    formStep.model.supplyConceptType.disabled =
        formStep.model.specialSupplyConceptAcknowledgement.disabled ||
        !formStep.model.specialSupplyConceptAcknowledgement.value;

    formStep.model.transferMeterType.disabled =
        formStep.model.specialSupplyConceptAcknowledgement.disabled ||
        !formStep.model.specialSupplyConceptAcknowledgement.value;

    formStep.model.measuringPointOperationInfoEwe.disabled =
        !isStorage(formStep.formModel) ||
        !(
            !formStep.model.measuringPointOperation.disabled &&
            formStep.model.measuringPointOperation.value === 'ewe'
        );
    formStep.model.measuringPointOperationInfoOther.disabled =
        !isStorage(formStep.formModel) ||
        !(
            !formStep.model.measuringPointOperation.disabled &&
            formStep.model.measuringPointOperation.value === 'other'
        );

    formStep.model.storageHeadline.disabled = !isPvAndStorageLte30(
        formStep.formModel,
    );

    const overallNominalPower =
        getOverallNominalPower(formStep.formModel.plantPerformance) ?? 0;

    formStep.model.minorConsumptionAllocation.disabled = !(
        formStep.model.operatingMode.value === 'fullInfeed' &&
        formStep.formModel.generationType.plantStructureType.value ===
            'building' &&
        overallNominalPower <= 100 &&
        formStep.formModel.powerConnection.doesConnectionExist.value === 'yes'
    );

    formStep.model.noExtraConsumption.disabled = !(
        !formStep.model.minorConsumptionAllocation.disabled &&
        formStep.model.minorConsumptionAllocation.value === true
    );

    formStep.model.operatorIdentityMatch.disabled = !(
        !formStep.model.minorConsumptionAllocation.disabled &&
        formStep.model.minorConsumptionAllocation.value === true
    );

    formStep.model.additionalMeterNumber.disabled = !(
        !formStep.model.minorConsumptionAllocation.disabled &&
        formStep.model.minorConsumptionAllocation.value === true
    );
};
